/**
 * Grid columns aligned with export_projects.json acf_groups (Project Summary Test 2, etc.).
 */

import React from "react";
import { isProjectFieldFileContentUrl, openAuthenticatedProjectFieldFile } from "./projectFieldFileAuth";
import {
  COMMERCIAL_DOMAINS,
  normalizeDepartments,
  normalizeMulti,
  serviceSpeedLabel,
} from "./plugins/commercialProjectSchema";
import { readCommercialDomainsForRow, repeaterToArray } from "./plugins/commercialProjectAcf";
import { readSubsegmentSummaryFromAcf, filterExpansionDepartments } from "./plugins/expansionProjectAcf";
import {
  compareExpansionWorkOrderDisplay,
  normalizeExpansionWorkOrderDisplay,
} from "./plugins/expansionWorkOrderFormat";
import { formatSubphaseLabel, readSubphaseForCurrentPhase, normalizeSubphaseSlug } from "./plugins/commercialSubphases";
import {
  aggregatePermitDates,
  permitsFileGridValue,
  countPermitApplications,
  countPermitIssued,
  formatPermitsRefundableDeposit,
  formatPermitsDepositAmount,
  readPermittingTrackingField,
} from "./plugins/commercialPermitsSchema";
import { formatDateOnlyDisplay } from "./plugins/dateOnlyDisplay";
import { commercialServiceOrderStoredValue } from "./plugins/commercialServiceOrderFields";
import {
  formatProjectMaterialsReview,
  formatProjectMaterialsSummary,
} from "./plugins/engineeringMaterialsTotals";
import { materialsInventoryPluginEnabled } from "./plugins/materialsInventoryAccess";
import { resolveContractOrAcfField, formatContractorsForRole } from "./plugins/contractsDerivedFields";
import { computeFootageProgressForProject } from "./plugins/expansionProgressUtils";

/** Commercial domain keys in acf_groups_json */
export const ACF = {
  SUMMARY: "project_summary",
  NET: "networking_details",
  FIBER: "fiber_details",
  ENG: "engineering_details",
  CONSTRUCTION: "construction_details",
  ACCOUNTING: "accounting_details",
  EXPANSION: "Expansion Segment Statistics",
  INVENTORY: "Inventory Test 2",
};

const ACF_SUMMARY_SNAKE = "project_summary";
const ACF_SUMMARY_LEGACY = "project_summary_test_2";
const ACF_SUMMARY_LEGACY_LABEL = "Project Summary Test 2";
const ACF_CONSTRUCTION_SNAKE = "construction_test_2";

export function summaryGroup(row) {
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return null;
  return (
    j[ACF.SUMMARY] ??
    j[ACF_SUMMARY_LEGACY_LABEL] ??
    j[ACF_SUMMARY_LEGACY] ??
  null
  );
}

/** Coerce DB / ACF booleans (true, "yes", 1, etc.). */
export function coerceProjectBool(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(s)) return true;
  if (["0", "false", "no", "n"].includes(s)) return false;
  return null;
}

/**
 * Heuristic when summary does not set cvin_expansion. Only clear segment slugs (seg-*).
 * Commercial revenue sub-WOs also use slugs like 14062-03-… — those must not infer expansion.
 */
export function inferExpansionFromShape(row) {
  if (!row || typeof row !== "object") return false;
  const slug = String(row.export_slug || "").trim().toLowerCase();
  return slug.startsWith("seg-");
}

function hasSubsegmentSummaryShape(row) {
  const j = row?.acf_groups_json;
  const sub = j?.subsegment_summary;
  return Boolean(sub && typeof sub === "object" && !Array.isArray(sub) && Object.keys(sub).length > 0);
}

/**
 * Effective CVIN expansion flag: DB column, subsegment_summary, project summary, then seg-* slug.
 * Stale project_summary.cvin_expansion=false must not override expansion subsegments.
 */
export function isCvinExpansion(row) {
  if (!row || typeof row !== "object") return false;
  const col = coerceProjectBool(row.cvin_expansion);
  if (col === true) return true;

  if (hasSubsegmentSummaryShape(row)) {
    const subFlag = coerceProjectBool(readSubsegmentSummaryFromAcf(row.acf_groups_json)?.cvin_expansion);
    if (subFlag === false) return false;
    return true;
  }

  const fromSummary = coerceProjectBool(summaryGroup(row)?.cvin_expansion);
  if (fromSummary === true) return true;
  if (col === false) return false;
  if (fromSummary === false) return false;
  return inferExpansionFromShape(row);
}

/** Multisite shell row (e.g. WO 14120); sub-locations use 14120.01, etc. */
export function isMultisiteParentRow(row) {
  const wo = row?.work_order != null ? String(row.work_order).trim() : "";
  return row?.multisite === true && wo !== "" && !wo.includes(".");
}

/** Map Project Summary phase label to subphases / subphases_exp object key */
const PHASE_LABEL_TO_SUBPHASE_KEY = {
  sales: "sales_subphase",
  finance: "finance_subphase",
  drafting: "drafting_subphase",
  engineering: "drafting_subphase",
  permitting: "permitting_subphase",
  construction: "construction_subphase",
  fiber: "fiber_subphase",
  networking: "networking_subphase",
  accounting: "accounting_subphase",
};

export function phaseToSubphaseKey(phaseLabel) {
  if (phaseLabel == null || phaseLabel === "") return null;
  const n = String(phaseLabel).trim().toLowerCase().replace(/\s+/g, "_");
  return PHASE_LABEL_TO_SUBPHASE_KEY[n] ?? null;
}

/** Phase label that drives subphases_exp vs subphases (matches Phase column rules for summary rows). */
export function phaseLabelForActiveSubphase(row) {
  const summary = summaryGroup(row);
  if (!summary || typeof summary !== "object") {
    if (row?.phase_label != null && String(row.phase_label).trim() !== "") {
      return String(row.phase_label).trim();
    }
    return "";
  }
  if (isCvinExpansion(row)) {
    const subSummary = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
    const phase =
      subSummary?.phase ??
      summary.phase_exp ??
      summary.phase;
    if (phase != null && String(phase).trim() !== "") return String(phase).trim();
    if (row?.phase_label != null && String(row.phase_label).trim() !== "") {
      return String(row.phase_label).trim();
    }
    return "";
  }
  if (summary.phase != null && String(summary.phase).trim() !== "") {
    return String(summary.phase).trim();
  }
  // Match Phase column: fall back to lifted phase_label when summary.phase is empty.
  if (row?.phase_label != null && String(row.phase_label).trim() !== "") {
    return String(row.phase_label).trim();
  }
  return "";
}

function engineeringGroup(row) {
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return null;
  return j[ACF.ENG] ?? j["Engineering Test 2"] ?? j.engineering_test_2 ?? null;
}

function isPermittingPhaseLabel(phaseLabel) {
  return String(phaseLabel || "")
    .trim()
    .toLowerCase() === "permitting";
}

/** Subphase slug for the active phase (commercial or expansion). */
export function currentSubphaseSlugForRow(row) {
  const phaseStr = phaseLabelForActiveSubphase(row);
  if (!phaseStr) return "";
  const summary = summaryGroup(row);
  if (!summary || typeof summary !== "object") return "";
  const expansion = isCvinExpansion(row);
  if (expansion) {
    const subSummary = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
    const subObj = subSummary?.subphases ?? summary.subphases_exp;
    if (!subObj || typeof subObj !== "object") {
      const direct = subSummary?.subphase ?? summary.subphase;
      return direct ? normalizeSubphaseSlug(direct) : "";
    }
    const key = phaseToSubphaseKey(phaseStr);
    if (!key) return "";
    return normalizeSubphaseSlug(subObj[key]);
  }
  const summaryForSub = {
    ...summary,
    phase:
      summary.phase != null && String(summary.phase).trim() !== "" ? summary.phase : phaseStr,
    status:
      summary.status != null && String(summary.status).trim() !== ""
        ? summary.status
        : row?.status,
  };
  return readSubphaseForCurrentPhase(summaryForSub);
}

function permittingSubphaseLabelWithCounts(row, slug) {
  const label = formatSubphaseLabel(slug);
  if (!isPermittingPhaseLabel(phaseLabelForActiveSubphase(row))) {
    return label;
  }
  const permits = engineeringGroup(row)?.permits;
  if (slug === "application_submitted") {
    const { applied, total } = countPermitApplications(permits);
    if (total <= 0) return label;
    return `${label} (${applied}/${total})`;
  }
  if (slug === "permit_received") {
    const { issued, total } = countPermitIssued(permits);
    if (total <= 0) return label;
    return `${label} (${issued}/${total})`;
  }
  return label;
}

/**
 * Single subphase for the active phase: subphases_exp + phase_exp when CVIN expansion,
 * else subphases + phase. Only the entry matching the active phase is shown (e.g. permitting_subphase when phase is Permitting).
 */
export function currentSubphaseForRow(row) {
  const slug = currentSubphaseSlugForRow(row);
  if (!slug) return "";
  return permittingSubphaseLabelWithCounts(row, slug);
}

function group(row, label) {
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return null;
  return j[label] ?? null;
}

function constructionGroup(row) {
  const j = row?.acf_groups_json;
  if (!j || typeof j !== "object") return null;
  return j[ACF.CONSTRUCTION] ?? j[ACF_CONSTRUCTION_SNAKE] ?? null;
}

function formatAcfScalar(v) {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return "";
  return String(v).trim();
}

/** Collect http(s) URLs from any nested JSON value */
export function extractHttpUrls(value, seen = new Set()) {
  const out = [];
  if (value === null || value === undefined) return out;
  if (typeof value === "string") {
    const t = value.trim();
    if (/^https?:\/\//i.test(t) && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
    return out;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      out.push(...extractHttpUrls(item, seen));
    }
    return out;
  }
  if (typeof value === "object") {
    for (const v of Object.values(value)) {
      out.push(...extractHttpUrls(v, seen));
    }
  }
  return out;
}

function formatNonUrlPrimitive(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    if (value.every((x) => typeof x === "string" && !/^https?:\/\//i.test(x))) {
      return value.join(", ");
    }
  }
  try {
    const s = JSON.stringify(value);
    return s.length > 200 ? `${s.slice(0, 197)}…` : s;
  } catch {
    return String(value);
  }
}

const CURRENCY_COL_IDS = new Set(["mrc", "non_recurring_charge"]);

export function formatGridCurrency(value) {
  if (value == null || value === "") return "";
  const n = Number(String(value).replace(/[$,]/g, "").trim());
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

/** Tiny "e" when Permit received is driven by an Extension rider date. */
function PermitReceivedDateCell(params) {
  const date = params.value != null ? String(params.value).trim() : "";
  if (!date) return "";
  const isExtension = Boolean(params.permitReceivedIsExtension?.(params));
  if (!isExtension) return date;
  return (
    <span title="Extension received date">
      {date}
      <sup
        aria-label="extension"
        style={{ fontSize: "0.65em", marginLeft: 2, fontWeight: 600, opacity: 0.75 }}
      >
        e
      </sup>
    </span>
  );
}

/** Columns sized to cell/header content instead of flex growth. */
export const AUTO_SIZE_CONTENT_COL_IDS = ["active_subphase"];

export function autoSizeContentFitGridColumns(api) {
  if (!api) return;
  const colIds = AUTO_SIZE_CONTENT_COL_IDS.filter((colId) => api.getColumn(colId)?.isVisible());
  if (colIds.length) api.autoSizeColumns(colIds);
}

const FileGlyph = (
  <svg className="cell-file-icon-svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
    <path
      fill="currentColor"
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2 5 5h-5V4zM8 12h8v2H8v-2zm0 4h8v2H8v-2z"
    />
  </svg>
);

/**
 * Renders http(s) links as file icons; otherwise shows text / JSON snippet.
 * Skipped for phase column via colId check.
 */
export function UrlOrValueCell(params) {
  const colId = params.column?.getColId();
  if (colId === "phase" || colId === "active_subphase") {
    return params.value != null && params.value !== "" ? String(params.value) : "";
  }

  const raw = params.value;
  const colField = params.column?.getColDef()?.field;
  const currencyCol =
    CURRENCY_COL_IDS.has(colId) || (colField && CURRENCY_COL_IDS.has(String(colField)));
  if (currencyCol) {
    return formatGridCurrency(raw);
  }

  const urls = extractHttpUrls(raw);
  if (urls.length > 0) {
    const earthAccept =
      colId === "preliminary_route" || colId === "route" || colId === "prelim_route" || colId === "exp_route"
        ? ".kmz,.kml"
        : undefined;
    return (
      <span className="cell-url-icons">
        {urls.map((href) => {
          const ours = isProjectFieldFileContentUrl(href);
          return (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="cell-file-link"
              title={href}
              onClick={
                ours
                  ? (e) => {
                      e.preventDefault();
                      void openAuthenticatedProjectFieldFile(href, {
                        accept: earthAccept,
                        filename: earthAccept ? "route.kmz" : undefined,
                      });
                    }
                  : undefined
              }
            >
              {FileGlyph}
            </a>
          );
        })}
      </span>
    );
  }

  return formatNonUrlPrimitive(raw);
}

/** AG Grid: sort numeric-looking strings (work orders: 9000 before 14000; 14120.01 before 14120.02). */
export function agGridNumericStringComparator(valueA, valueB) {
  const sa = valueA == null || valueA === "" ? "" : String(valueA).trim();
  const sb = valueB == null || valueB === "" ? "" : String(valueB).trim();
  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: "base" });
}

/** AG Grid: sort finite numbers; non-numeric values fall back to numeric string compare. */
export function agGridNumberComparator(valueA, valueB) {
  const na = Number(valueA);
  const nb = Number(valueB);
  const aNum = Number.isFinite(na);
  const bNum = Number.isFinite(nb);
  if (aNum && bNum) return na - nb;
  if (aNum) return -1;
  if (bNum) return 1;
  return agGridNumericStringComparator(valueA, valueB);
}

const NUMERIC_STRING_SORT_COL_IDS = new Set(["work_order", "work_order_base"]);
const NUMBER_SORT_COL_IDS = new Set([
  "id",
  "legacy_wp_post_id",
  "mrc",
  "non_recurring_charge",
  "term_in_months",
  "number_of_ips",
]);

/**
 * @param {object} opts
 * @param {(params: object) => React.ReactNode} opts.linkRenderer
 * @param {(row: object) => string} opts.displayPhaseForRow
 * @param {(params: object) => string|null} opts.phaseCellClass
 * @param {(params: object) => object|null} opts.phaseCellStyle
 * @param {(params: object) => object|null} opts.subphaseCellStyle
 * @param {boolean} [opts.includeMaterialsCols]
 * @param {boolean|((params: object) => boolean)} [opts.canEditPermittingFields]
 */
export function buildProjectColumnDefs({
  linkRenderer,
  displayPhaseForRow,
  phaseCellClass,
  phaseCellStyle,
  subphaseCellStyle,
  includeMaterialsCols = false,
  canEditPermittingFields = false,
}) {
  const permittingEditable =
    typeof canEditPermittingFields === "function"
      ? canEditPermittingFields
      : () => Boolean(canEditPermittingFields);  const v = {
    activeSubphase: (p) => currentSubphaseForRow(p.data),
    dept: (p) => {
      const s = summaryGroup(p.data);
      if (!s) return "";
      const deps = normalizeDepartments(s.departments ?? s.department_involvement);
      return deps.length ? deps.join(", ") : "";
    },
    connType: (p) => {
      const net = group(p.data, ACF.NET);
      const summary = summaryGroup(p.data);
      const conn = normalizeMulti(net?.connection ?? summary?.connection_type);
      return conn.length ? conn.join(", ") : "";
    },
    itBlock: (p) => {
      const s = summaryGroup(p.data);
      if (!s) return "";
      const parts = [
        s.it_contact_name,
        s.it_contact_email,
        s.it_contact_number,
      ].filter(Boolean);
      return parts.join(" · ");
    },
    onsiteBlock: (p) => {
      const s = summaryGroup(p.data);
      if (!s) return "";
      const parts = [
        s.onsite_contact_name,
        s.onsite_contact_email,
        s.onsite_contact_number,
      ].filter(Boolean);
      return parts.join(" · ");
    },
    acctBlock: (p) => {
      const s = summaryGroup(p.data);
      if (!s) return "";
      const parts = [
        s.accounting_contact_name,
        s.accounting_contact_email,
        s.accounting_contact_number,
      ].filter(Boolean);
      return parts.join(" · ");
    },
  };

  return [
    { field: "id", headerName: "ID", sortable: true, filter: true, width: 90 },
    {
      field: "legacy_wp_post_id",
      headerName: "WP ID",
      sortable: true,
      filter: true,
      width: 90,
    },
    {
      field: "work_order",
      headerName: "Work order",
      sortable: true,
      filter: true,
      cellRenderer: linkRenderer,
    },
    {
      field: "work_order_base",
      headerName: "WO base",
      sortable: true,
      filter: true,
      width: 100,
    },
    {
      field: "name",
      headerName: "Project",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 160,
      cellRenderer: linkRenderer,
    },
    { field: "export_slug", headerName: "Slug", filter: true, width: 160 },
    { field: "customer", headerName: "Customer", editable: true, filter: true, minWidth: 200 },
    { field: "status", headerName: "Status", editable: true, filter: true, width: 110 },
    {
      field: "cvin_expansion",
      headerName: "CVIN exp.",
      sortable: true,
      filter: true,
      width: 100,
      valueGetter: (params) => isCvinExpansion(params.data),
    },
    {
      field: "phase",
      headerName: "Phase",
      sortable: true,
      filter: true,
      width: 130,
      valueGetter: (params) => displayPhaseForRow(params.data),
      cellClass: phaseCellClass,
      cellStyle: phaseCellStyle,
    },
    {
      colId: "active_subphase",
      headerName: "Subphase",
      filter: true,
      minWidth: 80,
      valueGetter: v.activeSubphase,
      cellStyle: subphaseCellStyle,
    },
    { field: "project_type", headerName: "WO type", filter: true, width: 110 },
    { field: "subtype", headerName: "Choices", filter: true, width: 120 },
    {
      colId: "department",
      headerName: "Departments",
      filter: true,
      flex: 1,
      minWidth: 180,
      valueGetter: v.dept,
    },
    { field: "site_name", headerName: "Site name", filter: true, width: 140 },
    {
      field: "service_location",
      headerName: "Service location",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 200,
    },
    { field: "a_location", headerName: "A loc", filter: true, width: 120 },
    { field: "z_location", headerName: "Z loc", filter: true, width: 120 },
    {
      colId: "service_speed",
      headerName: "Service speed",
      filter: true,
      width: 110,
      valueGetter: (p) => {
        const summary = summaryGroup(p.data);
        const net = group(p.data, ACF.NET);
        return serviceSpeedLabel(net?.service_speed ?? summary?.service_speed ?? "");
      },
    },
    {
      colId: "connection_type",
      headerName: "Connection",
      filter: true,
      width: 120,
      valueGetter: v.connType,
    },
    {
      colId: "handoff_type",
      headerName: "Handoff",
      filter: true,
      width: 100,
      valueGetter: (p) => {
        const net = group(p.data, ACF.NET);
        const summary = summaryGroup(p.data);
        return net?.handoff ?? summary?.handoff_type ?? summary?.handoff ?? "";
      },
    },
    {
      colId: "number_of_ips",
      headerName: "# IPs",
      filter: true,
      width: 140,
      valueGetter: (p) => {
        const net = group(p.data, ACF.NET);
        const summary = summaryGroup(p.data);
        return net?.ips ?? summary?.number_of_ips ?? summary?.ips ?? "";
      },
    },
    { field: "mrc", headerName: "MRC", sortable: true, filter: true, width: 100 },
    {
      field: "non_recurring_charge",
      headerName: "NRC",
      sortable: true,
      filter: true,
      width: 100,
    },
    { field: "term_in_months", headerName: "Term (mo)", sortable: true, filter: true, width: 100 },
    {
      field: "invoiced_date",
      headerName: "Invoiced",
      sortable: true,
      filter: true,
      width: 110,
    },
    { field: "sales_rep", headerName: "Sales rep", editable: true, filter: true, width: 130 },
    {
      colId: "it_contact",
      headerName: "IT contact",
      filter: true,
      flex: 1,
      minWidth: 180,
      valueGetter: v.itBlock,
    },
    {
      colId: "onsite_contact",
      headerName: "Onsite contact",
      filter: true,
      flex: 1,
      minWidth: 180,
      valueGetter: v.onsiteBlock,
    },
    {
      colId: "accounting_contact",
      headerName: "Accounting contact",
      filter: true,
      flex: 1,
      minWidth: 180,
      valueGetter: v.acctBlock,
    },
    {
      colId: "service_order",
      headerName: "Service order",
      width: 72,
      // Fall back across service_order / executed / legacy countersigned (same as export).
      valueGetter: (p) => commercialServiceOrderStoredValue(summaryGroup(p.data)),
    },
    {
      colId: "countersigned_so",
      headerName: "Signed SO",
      width: 72,
      valueGetter: (p) => commercialServiceOrderStoredValue(summaryGroup(p.data)),
    },
    {
      colId: "work_order_numbers_field",
      headerName: "WO # file",
      width: 72,
      valueGetter: (p) => summaryGroup(p.data)?.work_order_numbers ?? "",
    },
    {
      colId: "circuit_id",
      headerName: "Circuit ID",
      filter: true,
      width: 130,
      valueGetter: (p) => group(p.data, ACF.NET)?.circuit_id ?? "",
    },
    {
      colId: "cut_sheet",
      headerName: "Cut sheet",
      width: 72,
      valueGetter: (p) => group(p.data, ACF.NET)?.cut_sheet ?? "",
    },
    {
      colId: "nid_config",
      headerName: "NID cfg",
      width: 72,
      valueGetter: (p) => group(p.data, ACF.NET)?.nid_config ?? "",
    },
    {
      colId: "topology",
      headerName: "Topology",
      width: 72,
      valueGetter: (p) => group(p.data, ACF.NET)?.topology ?? "",
    },
    {
      colId: "preliminary_route",
      headerName: "Prelim route",
      width: 72,
      valueGetter: (p) => group(p.data, ACF.ENG)?.preliminary_route ?? "",
    },
    {
      colId: "site_walk_files",
      headerName: "Site walk",
      width: 88,
      valueGetter: (p) => {
        const v = group(p.data, ACF.ENG)?.site_walk_files;
        if (v == null || v === "") return "";
        if (Array.isArray(v)) {
          return v.length === 0 ? "" : `${v.length} file${v.length === 1 ? "" : "s"}`;
        }
        return v;
      },
    },
    {
      colId: "permit_files",
      headerName: "Permits",
      width: 72,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG);
        return permitsFileGridValue(eng?.permits, eng?.permit_files);
      },
    },
    {
      colId: "permit_received_date",
      headerName: "Permit received",
      filter: true,
      width: 128,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG);
        const domains = readCommercialDomainsForRow(p.data, p.data?.acf_domains);
        const permits = eng?.permits ?? domains?.[ACF.ENG]?.permits;
        return aggregatePermitDates(permits).permit_received_date;
      },
      cellRenderer: PermitReceivedDateCell,
      cellRendererParams: {
        permitReceivedIsExtension: (p) => {
          const eng = group(p.data, ACF.ENG);
          const domains = readCommercialDomainsForRow(p.data, p.data?.acf_domains);
          const permits = eng?.permits ?? domains?.[ACF.ENG]?.permits;
          return aggregatePermitDates(permits).permit_received_is_extension;
        },
      },
    },
    {
      colId: "permit_expiration_date",
      headerName: "Permit expires",
      filter: true,
      width: 120,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG);
        const domains = readCommercialDomainsForRow(p.data, p.data?.acf_domains);
        const permits = eng?.permits ?? domains?.[ACF.ENG]?.permits;
        return aggregatePermitDates(permits).permit_expiration_date;
      },
    },
    {
      colId: "refundable_deposit",
      headerName: "Refundable deposit",
      filter: true,
      width: 140,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG);
        const domains = readCommercialDomainsForRow(p.data, p.data?.acf_domains);
        const permits = eng?.permits ?? domains?.[ACF.ENG]?.permits;
        return formatPermitsRefundableDeposit(permits);
      },
    },
    {
      colId: "deposit_amount",
      headerName: "Deposit amount",
      filter: true,
      width: 130,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG);
        const domains = readCommercialDomainsForRow(p.data, p.data?.acf_domains);
        const permits = eng?.permits ?? domains?.[ACF.ENG]?.permits;
        return formatPermitsDepositAmount(permits);
      },
    },
    {
      colId: "permit_status",
      headerName: "Permit Status",
      filter: true,
      editable: permittingEditable,
      flex: 1,
      minWidth: 160,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG) || engineeringGroup(p.data);
        return readPermittingTrackingField(eng, "permit_status");
      },
      valueSetter: (p) => {
        if (!p.data) return false;
        const engKey = ACF.ENG;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const eng = { ...(ag[engKey] || engineeringGroup(p.data) || {}) };
        eng.permit_status = p.newValue == null ? "" : String(p.newValue);
        ag[engKey] = eng;
        p.data.acf_groups_json = ag;
        return true;
      },
    },
    {
      colId: "revision_requested_date",
      headerName: "Revision Requested",
      filter: true,
      editable: permittingEditable,
      width: 140,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG) || engineeringGroup(p.data);
        return readPermittingTrackingField(eng, "revision_requested_date");
      },
      valueFormatter: (p) => formatDateOnlyDisplay(p.value) || "",
      valueSetter: (p) => {
        if (!p.data) return false;
        const engKey = ACF.ENG;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const eng = { ...(ag[engKey] || engineeringGroup(p.data) || {}) };
        eng.revision_requested_date = p.newValue == null ? "" : String(p.newValue).trim();
        ag[engKey] = eng;
        p.data.acf_groups_json = ag;
        return true;
      },
    },
    {
      colId: "permit_follow_up",
      headerName: "Follow-up",
      filter: true,
      editable: permittingEditable,
      width: 120,
      valueGetter: (p) => {
        const eng = group(p.data, ACF.ENG) || engineeringGroup(p.data);
        return readPermittingTrackingField(eng, "permit_follow_up");
      },
      valueFormatter: (p) => formatDateOnlyDisplay(p.value) || "",
      valueSetter: (p) => {
        if (!p.data) return false;
        const engKey = ACF.ENG;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const eng = { ...(ag[engKey] || engineeringGroup(p.data) || {}) };
        eng.permit_follow_up = p.newValue == null ? "" : String(p.newValue).trim();
        ag[engKey] = eng;
        p.data.acf_groups_json = ag;
        return true;
      },
    },
    ...(includeMaterialsCols
      ? [
          {
            colId: "materials_summary",
            headerName: "Materials",
            filter: true,
            flex: 1,
            minWidth: 160,
            valueGetter: (p) => formatProjectMaterialsSummary(p.data),
          },
          {
            colId: "materials_review",
            headerName: "Materials review",
            filter: true,
            width: 130,
            valueGetter: (p) => formatProjectMaterialsReview(p.data),
          },
        ]
      : []),
    {
      colId: "usa_tickets",
      headerName: "USA",
      width: 72,
      valueGetter: (p) => group(p.data, ACF.ENG)?.usa ?? "",
    },
    {
      colId: "asbuilts",
      headerName: "Asbuilts",
      width: 72,
      valueGetter: (p) => group(p.data, ACF.ENG)?.asbuilts_redlines ?? "",
    },
    {
      colId: "drafter",
      headerName: "Assigned drafter",
      filter: true,
      sortable: true,
      width: 200,
      valueGetter: (p) => {
        const row = p.data;
        if (!row) return "";
        const domains = readCommercialDomainsForRow(row, row.acf_domains);
        const v = domains?.[ACF.ENG]?.drafter;
        return v != null ? String(v).trim() : "";
      },
    },
    {
      colId: "permitting_specialist",
      headerName: "Assigned permitting specialist",
      filter: true,
      sortable: true,
      width: 220,
      valueGetter: (p) => {
        const row = p.data;
        if (!row) return "";
        const domains = readCommercialDomainsForRow(row, row.acf_domains);
        const v = domains?.[ACF.ENG]?.permitting_specialist;
        return v != null ? String(v).trim() : "";
      },
    },
    {
      colId: "splicer",
      headerName: "Splicer",
      filter: true,
      width: 180,
      valueGetter: (p) => {
        const row = p.data;
        if (!row) return "";
        const domains = readCommercialDomainsForRow(row, row.acf_domains);
        const splicer = domains?.[ACF.FIBER]?.splicer;
        return splicer != null ? String(splicer).trim() : "";
      },
    },
    {
      colId: "assigned_network_tech",
      headerName: "Assigned network tech",
      filter: true,
      sortable: true,
      width: 200,
      valueGetter: (p) => {
        const row = p.data;
        if (!row) return "";
        const domains = readCommercialDomainsForRow(row, row.acf_domains);
        const net = domains?.[ACF.NET] || {};
        const legacy = net.assigned_technician != null ? String(net.assigned_technician).trim() : "";
        if (legacy) return legacy;
        const installs = Array.isArray(net.installations)
          ? net.installations
          : net.installations && typeof net.installations === "object"
            ? Object.keys(net.installations)
                .filter((k) => /^\d+$/.test(k))
                .sort((a, b) => Number(a) - Number(b))
                .map((k) => net.installations[k])
            : [];
        const techs = installs
          .map((r) => (r?.technician != null ? String(r.technician).trim() : ""))
          .filter(Boolean);
        return [...new Set(techs)].join(", ");
      },
    },
    {
      colId: "fiber_files",
      headerName: "Fiber files",
      width: 72,
      valueGetter: (p) => group(p.data, ACF.FIBER)?.fiber_files ?? "",
    },
    {
      colId: "bid_out",
      headerName: "Bid out",
      sortable: true,
      filter: true,
      width: 90,
      valueGetter: (p) => formatAcfScalar(constructionGroup(p.data)?.bid_out),
    },
    {
      colId: "contractor",
      headerName: "Contractor (build)",
      sortable: true,
      filter: true,
      width: 170,
      valueGetter: (p) => {
        const g = constructionGroup(p.data);
        return formatAcfScalar(g?.construction_contractor ?? g?.contractor);
      },
    },
    {
      colId: "contractor_fiber",
      headerName: "Contractor (fiber)",
      sortable: true,
      filter: true,
      width: 170,
      valueGetter: (p) => {
        const g = constructionGroup(p.data);
        return formatAcfScalar(g?.fiber_contractor ?? g?.contractor_fiber);
      },
    },
    {
      field: "project_notes",
      headerName: "Notes",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 200,
    },
    {
      field: "site_walk",
      headerName: "Site walk (flag)",
      sortable: true,
      filter: true,
      width: 130,
    },
    {
      field: "numbers_complete",
      headerName: "Numbers",
      sortable: true,
      filter: true,
      width: 100,
    },
    {
      field: "countersigned",
      headerName: "Countersigned",
      sortable: true,
      filter: true,
      width: 120,
    },
    { field: "created_at", headerName: "Created", sortable: true, filter: true, width: 180 },
  ].map((def) => {
    const colId = String(def.colId ?? def.field ?? "");
    let next = def;
    if (NUMERIC_STRING_SORT_COL_IDS.has(colId)) {
      next = { ...next, comparator: agGridNumericStringComparator };
    } else if (NUMBER_SORT_COL_IDS.has(colId)) {
      next = { ...next, comparator: agGridNumberComparator };
    }
    if (next.field === "phase" || next.cellRenderer) {
      return next;
    }
    return { ...next, cellRenderer: UrlOrValueCell };
  });
}

/**
 * Stable column ids + labels for User Matrix and admin UIs (no React renderers required).
 */
export function getProjectGridColumnDescriptors() {
  const defs = buildProjectColumnDefs({
    linkRenderer: () => null,
    displayPhaseForRow: () => "",
    phaseCellClass: () => "",
    phaseCellStyle: () => null,
    subphaseCellStyle: () => null,
    includeMaterialsCols: materialsInventoryPluginEnabled(),
  });
  return defs
    .map((c) => ({
      colId: String(c.colId ?? c.field ?? ""),
      headerName: c.headerName || c.field || c.colId || "",
    }))
    .filter((x) => x.colId && x.colId !== "__select__")
    .sort((a, b) => a.headerName.localeCompare(b.headerName, undefined, { sensitivity: "base" }));
}

function expansionFormatAcfScalar(v) {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return "";
  return String(v).trim();
}

function expansionSubSummary(row) {
  return readSubsegmentSummaryFromAcf(row?.acf_groups_json);
}

/** Parent segment WO label (e.g. base 6784 + segment 10 → 6784.10, not 6784.1). */
export function expansionParentWorkOrderDisplay(woBase, segment) {
  const base = String(woBase ?? "").trim();
  const seg = String(segment ?? "").trim();
  if (!base || base.startsWith("(No")) return base || "";
  if (!seg) return base;
  const intBase = base.includes(".") ? base.split(".")[0] : base;
  return `${intBase}.${seg}`;
}

/** Display work order without stripping trailing segment zeros (6784.10 not 6784.1). */
export function formatExpansionWorkOrderForDisplay(row) {
  const sub = expansionSubSummary(row);
  const woRaw = sub?.work_order ?? row?.work_order ?? "";
  return normalizeExpansionWorkOrderDisplay(woRaw);
}

function expansionDomains(row) {
  return readCommercialDomainsForRow(row, row?.acf_domains);
}

function expansionDepartments(row) {
  const sub = expansionSubSummary(row);
  return normalizeDepartments(sub?.departments ?? sub?.department_involvement ?? []);
}

function formatExpansionPermitsSummary(permitsRaw) {
  const rows = repeaterToArray(permitsRaw);
  if (!rows.length) return "";
  const agencies = rows
    .map((r) => (r?.agency != null ? String(r.agency).trim() : ""))
    .filter(Boolean);
  const head = agencies[0] || "Permit";
  return rows.length === 1 ? head : `${head} (+${rows.length - 1})`;
}

function formatExpansionFiberOtdrSummary(fiberFiles) {
  if (fiberFiles && typeof fiberFiles === "object" && !Array.isArray(fiberFiles)) {
    const parts = [];
    for (const loc of ["A", "Z"]) {
      const otdr = fiberFiles[loc]?.otdr;
      if (otdr != null && String(otdr).trim() !== "") parts.push(`${loc} OTDR`);
    }
    if (parts.length) return parts.join(" · ");
  }
  const rows = repeaterToArray(fiberFiles);
  if (!rows.length) return "";
  return rows
    .map((r, i) => {
      const loc = r?.location != null ? String(r.location).trim() : "";
      const hasOtdr = r?.otdr != null && String(r.otdr).trim() !== "";
      if (loc) return hasOtdr ? `${loc} OTDR` : loc;
      return hasOtdr ? `Run ${i + 1} OTDR` : `Run ${i + 1}`;
    })
    .join(" · ");
}

const EXPANSION_NUMERIC_STRING_SORT_COL_IDS = new Set(["work_order"]);
const EXPANSION_NUMBER_SORT_COL_IDS = new Set([
  "exp_construction_footage",
  "exp_override_footage",
  "exp_caltrans_footage",
  "exp_completed_conduit_footage",
  "exp_completed_fiber_footage",
  "exp_handholes",
]);

/** Columns that show their own values on expansion group header rows (colspan stops before these). */
export const EXPANSION_GROUP_ROLLUP_COL_IDS = new Set([
  "exp_construction_footage",
  "exp_override_footage",
  "exp_completed_conduit_footage",
  "exp_completed_fiber_footage",
]);

function formatExpansionInstalledFootage(value) {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/**
 * Expansion (CVIN) grid columns: subsegment_summary + engineering/construction/fiber/accounting domains.
 */
export function buildExpansionColumnDefs({
  linkRenderer,
  displayPhaseForRow,
  phaseCellClass,
  phaseCellStyle,
  subphaseCellStyle,
  includeContractGroupCols = false,
  includeMaterialsCols = false,
  canEditPermittingFields = false,
  canEditEngineeringFields = false,
}) {
  const permittingEditable =
    typeof canEditPermittingFields === "function"
      ? canEditPermittingFields
      : () => Boolean(canEditPermittingFields);
  const engineeringEditable =
    typeof canEditEngineeringFields === "function"
      ? canEditEngineeringFields
      : () => Boolean(canEditEngineeringFields);
  const cols = [
    {
      colId: "work_order",
      field: "work_order",
      headerName: "Work order",
      sortable: true,
      filter: true,
      width: 110,
      cellRenderer: linkRenderer,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") {
          return p.data._groupWoBase ?? p.data._groupKey ?? "";
        }
        return formatExpansionWorkOrderForDisplay(p.data);
      },
      comparator: (a, b, nodeA, nodeB) =>
        compareExpansionWorkOrderDisplay(
          formatExpansionWorkOrderForDisplay(nodeA?.data),
          formatExpansionWorkOrderForDisplay(nodeB?.data)
        ),
    },
    {
      colId: "exp_segment",
      headerName: "Segment",
      sortable: true,
      filter: true,
      width: 88,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") {
          return p.data._groupSegment ?? "";
        }
        return expansionSubSummary(p.data)?.segment ?? "";
      },
    },
    ...(includeContractGroupCols
      ? [
          {
            colId: "exp_group_subsegments",
            headerName: "Subsegments",
            sortable: true,
            filter: "agNumberColumnFilter",
            width: 110,
            valueGetter: (p) =>
              p.data?._rowType === "group" ? p.data._childCount ?? "" : "",
          },
          {
            colId: "exp_group_awarded_footage",
            headerName: "Awarded ft",
            sortable: true,
            filter: "agNumberColumnFilter",
            width: 105,
            valueGetter: (p) => {
              if (p.data?._rowType !== "group") return "";
              const v = p.data._groupAwardRollups?.awardedFootage;
              return v != null ? v : "";
            },
            valueFormatter: (p) =>
              p.value === "" || p.value == null
                ? ""
                : Number(p.value).toLocaleString(undefined, { maximumFractionDigits: 0 }),
          },
          {
            colId: "exp_group_awarded_cost",
            headerName: "Awarded amount",
            sortable: true,
            filter: "agNumberColumnFilter",
            width: 130,
            valueGetter: (p) => {
              if (p.data?._rowType !== "group") return "";
              const v = p.data._groupAwardRollups?.awardedContractorCost;
              return v != null ? v : "";
            },
            valueFormatter: (p) => formatGridCurrency(p.value),
          },
        ]
      : []),
    {
      colId: "exp_project_name",
      headerName: "Project name",
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 160,
      valueGetter: (p) => {
        const sub = expansionSubSummary(p.data);
        return sub?.project_name ?? p.data?.name ?? "";
      },
    },
    {
      colId: "exp_status",
      headerName: "Status",
      sortable: true,
      filter: true,
      width: 110,
      valueGetter: (p) => expansionSubSummary(p.data)?.status ?? p.data?.status ?? "",
    },
    {
      field: "phase",
      headerName: "Phase",
      sortable: true,
      filter: true,
      width: 120,
      valueGetter: (p) => displayPhaseForRow(p.data),
      cellClass: phaseCellClass,
      cellStyle: phaseCellStyle,
    },
    {
      colId: "active_subphase",
      headerName: "Subphase",
      filter: true,
      minWidth: 80,
      valueGetter: (p) => currentSubphaseForRow(p.data),
      cellStyle: subphaseCellStyle,
    },
    {
      colId: "exp_departments",
      headerName: "Departments",
      filter: true,
      flex: 1,
      minWidth: 180,
      valueGetter: (p) => {
        const sub = expansionSubSummary(p.data);
        const deps = filterExpansionDepartments(sub?.departments ?? sub?.department_involvement ?? []);
        return deps.length ? deps.join(", ") : "";
      },
    },
    {
      colId: "exp_bid_out",
      headerName: "Bid out",
      sortable: true,
      filter: true,
      width: 90,
      valueGetter: (p) => expansionFormatAcfScalar(expansionSubSummary(p.data)?.bid_out),
    },
    {
      colId: "exp_construction_footage",
      headerName: "Construction ft",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 115,
      editable: (p) => p.data?._rowType !== "group" && engineeringEditable(p),
      valueGetter: (p) => {
        if (p.data?._rowType === "group") {
          const v = p.data._groupStats?.constructionFootageTotal;
          return v != null ? v : "";
        }
        return expansionSubSummary(p.data)?.construction_footage ?? "";
      },
      valueFormatter: (p) => formatExpansionInstalledFootage(p.value),
      cellRenderer: (p) => formatExpansionInstalledFootage(p.value),
      valueSetter: (p) => {
        if (!p.data || p.data._rowType === "group") return false;
        const next =
          p.newValue == null || String(p.newValue).trim() === ""
            ? ""
            : String(p.newValue).trim().replace(/,/g, "");
        if (next !== "" && !Number.isFinite(Number(next))) return false;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const subKey = "subsegment_summary";
        const statsKey = "expansion_segment_statistics";
        const consKey = COMMERCIAL_DOMAINS.CONSTRUCTION;
        const sub = { ...(ag[subKey] || {}) };
        sub.construction_footage = next;
        ag[subKey] = sub;
        const stats = { ...(ag[statsKey] || {}) };
        stats.construction_footage = next;
        ag[statsKey] = stats;
        const cons = { ...(ag[consKey] || {}) };
        cons.reported_footage = next;
        ag[consKey] = cons;
        p.data.acf_groups_json = ag;
        return true;
      },
      cellClass: (p) =>
        p.data?._rowType === "group" ? "expansion-group-rollup-cell" : undefined,
    },
    {
      colId: "exp_override_footage",
      headerName: "Override ft",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 105,
      editable: (p) => p.data?._rowType !== "group" && engineeringEditable(p),
      valueGetter: (p) => {
        if (p.data?._rowType === "group") {
          const v = p.data._groupStats?.overrideFootageTotal;
          return v != null ? v : "";
        }
        return expansionSubSummary(p.data)?.override_footage ?? "";
      },
      valueFormatter: (p) => formatExpansionInstalledFootage(p.value),
      cellRenderer: (p) => formatExpansionInstalledFootage(p.value),
      valueSetter: (p) => {
        if (!p.data || p.data._rowType === "group") return false;
        const next =
          p.newValue == null || String(p.newValue).trim() === ""
            ? ""
            : String(p.newValue).trim().replace(/,/g, "");
        if (next !== "" && !Number.isFinite(Number(next))) return false;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const subKey = "subsegment_summary";
        const statsKey = "expansion_segment_statistics";
        const sub = { ...(ag[subKey] || {}) };
        sub.override_footage = next;
        ag[subKey] = sub;
        const stats = { ...(ag[statsKey] || {}) };
        stats.override_footage = next;
        ag[statsKey] = stats;
        p.data.acf_groups_json = ag;
        return true;
      },
      cellClass: (p) =>
        p.data?._rowType === "group" ? "expansion-group-rollup-cell" : undefined,
    },
    {
      colId: "exp_caltrans_footage",
      headerName: "Caltrans ft",
      sortable: true,
      filter: true,
      width: 100,
      valueGetter: (p) => expansionSubSummary(p.data)?.caltrans_footage ?? "",
    },
    {
      colId: "exp_handholes",
      headerName: "Handholes",
      sortable: true,
      filter: true,
      width: 95,
      valueGetter: (p) => expansionSubSummary(p.data)?.handholes ?? "",
    },
    {
      colId: "exp_completed_conduit_footage",
      headerName: "Conduit installed",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 130,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") {
          const v = p.data._groupStats?.completedConduitFootageTotal;
          return v != null ? v : "";
        }
        if (!p.data || p.data._rowType === "group") return "";
        const n = computeFootageProgressForProject(p.data)?.conduitCompleted;
        return n != null && Number.isFinite(n) ? n : "";
      },
      valueFormatter: (p) => formatExpansionInstalledFootage(p.value),
      cellRenderer: (p) => formatExpansionInstalledFootage(p.value),
      cellClass: (p) =>
        p.data?._rowType === "group" ? "expansion-group-rollup-cell" : undefined,
    },
    {
      colId: "exp_completed_fiber_footage",
      headerName: "Fiber installed",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 120,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") {
          const v = p.data._groupStats?.completedFiberFootageTotal;
          return v != null ? v : "";
        }
        if (!p.data || p.data._rowType === "group") return "";
        const n = computeFootageProgressForProject(p.data)?.fiberCompleted;
        return n != null && Number.isFinite(n) ? n : "";
      },
      valueFormatter: (p) => formatExpansionInstalledFootage(p.value),
      cellRenderer: (p) => formatExpansionInstalledFootage(p.value),
      cellClass: (p) =>
        p.data?._rowType === "group" ? "expansion-group-rollup-cell" : undefined,
    },
    {
      colId: "exp_route",
      headerName: "Route",
      filter: true,
      width: 72,
      valueGetter: (p) => {
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return eng?.route ?? eng?.preliminary_route ?? "";
      },
    },
    {
      colId: "exp_drafter",
      headerName: "Drafter",
      filter: true,
      width: 140,
      valueGetter: (p) => {
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        const v = eng?.drafter;
        return v != null ? String(v).trim() : "";
      },
    },
    {
      colId: "exp_permitting_specialist",
      headerName: "Permitting specialist",
      filter: true,
      width: 180,
      valueGetter: (p) => {
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        const v = eng?.permitting_specialist;
        return v != null ? String(v).trim() : "";
      },
    },
    {
      colId: "exp_permits",
      headerName: "Permits",
      filter: true,
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => {
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return formatExpansionPermitsSummary(eng?.permits);
      },
    },
    {
      colId: "exp_permit_received_date",
      headerName: "Permit received",
      filter: true,
      width: 128,
      valueGetter: (p) => {
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return aggregatePermitDates(eng?.permits).permit_received_date;
      },
      cellRenderer: PermitReceivedDateCell,
      cellRendererParams: {
        permitReceivedIsExtension: (p) => {
          const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
          return aggregatePermitDates(eng?.permits).permit_received_is_extension;
        },
      },
    },
    {
      colId: "exp_permit_expiration_date",
      headerName: "Permit expires",
      filter: true,
      width: 120,
      valueGetter: (p) => {
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return aggregatePermitDates(eng?.permits).permit_expiration_date;
      },
    },
    {
      colId: "exp_refundable_deposit",
      headerName: "Refundable deposit",
      filter: true,
      width: 140,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") return "";
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return formatPermitsRefundableDeposit(eng?.permits);
      },
    },
    {
      colId: "exp_deposit_amount",
      headerName: "Deposit amount",
      filter: true,
      width: 130,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") return "";
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return formatPermitsDepositAmount(eng?.permits);
      },
    },
    {
      colId: "exp_permit_status",
      headerName: "Permit Status",
      filter: true,
      editable: (p) => p.data?._rowType !== "group" && permittingEditable(p),
      flex: 1,
      minWidth: 160,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") return "";
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return readPermittingTrackingField(eng, "permit_status");
      },
      valueSetter: (p) => {
        if (!p.data || p.data._rowType === "group") return false;
        const engKey = COMMERCIAL_DOMAINS.ENGINEERING;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const eng = { ...(ag[engKey] || {}) };
        eng.permit_status = p.newValue == null ? "" : String(p.newValue);
        ag[engKey] = eng;
        p.data.acf_groups_json = ag;
        return true;
      },
    },
    {
      colId: "exp_revision_requested_date",
      headerName: "Revision Requested",
      filter: true,
      editable: (p) => p.data?._rowType !== "group" && permittingEditable(p),
      width: 140,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") return "";
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return readPermittingTrackingField(eng, "revision_requested_date");
      },
      valueFormatter: (p) => formatDateOnlyDisplay(p.value) || "",
      valueSetter: (p) => {
        if (!p.data || p.data._rowType === "group") return false;
        const engKey = COMMERCIAL_DOMAINS.ENGINEERING;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const eng = { ...(ag[engKey] || {}) };
        eng.revision_requested_date = p.newValue == null ? "" : String(p.newValue).trim();
        ag[engKey] = eng;
        p.data.acf_groups_json = ag;
        return true;
      },
    },
    {
      colId: "exp_permit_follow_up",
      headerName: "Follow-up",
      filter: true,
      editable: (p) => p.data?._rowType !== "group" && permittingEditable(p),
      width: 120,
      valueGetter: (p) => {
        if (p.data?._rowType === "group") return "";
        const eng = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ENGINEERING];
        return readPermittingTrackingField(eng, "permit_follow_up");
      },
      valueFormatter: (p) => formatDateOnlyDisplay(p.value) || "",
      valueSetter: (p) => {
        if (!p.data || p.data._rowType === "group") return false;
        const engKey = COMMERCIAL_DOMAINS.ENGINEERING;
        const ag =
          p.data.acf_groups_json && typeof p.data.acf_groups_json === "object"
            ? { ...p.data.acf_groups_json }
            : {};
        const eng = { ...(ag[engKey] || {}) };
        eng.permit_follow_up = p.newValue == null ? "" : String(p.newValue).trim();
        ag[engKey] = eng;
        p.data.acf_groups_json = ag;
        return true;
      },
    },
    ...(includeMaterialsCols
      ? [
          {
            colId: "exp_materials_summary",
            headerName: "Materials",
            filter: true,
            flex: 1,
            minWidth: 160,
            valueGetter: (p) => {
              if (p.data?._rowType === "group") return "";
              return formatProjectMaterialsSummary(p.data);
            },
          },
          {
            colId: "exp_materials_review",
            headerName: "Materials review",
            filter: true,
            width: 130,
            valueGetter: (p) => {
              if (p.data?._rowType === "group") return "";
              return formatProjectMaterialsReview(p.data);
            },
          },
        ]
      : []),
    {
      colId: "exp_inspector",
      headerName: "Inspector",
      filter: true,
      width: 130,
      valueGetter: (p) =>
        expansionFormatAcfScalar(expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION]?.inspector),
    },
    {
      colId: "exp_construction_contractor",
      headerName: "Construction contractor",
      filter: true,
      width: 170,
      valueGetter: (p) => {
        const g = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION];
        const summary = p.context?.contractFieldsByProjectId?.[p.data?.id];
        const fromContracts = formatContractorsForRole(summary, "construction");
        if (summary?.fields && Object.prototype.hasOwnProperty.call(summary.fields, "construction_contractor")) {
          return expansionFormatAcfScalar(fromContracts || summary.fields.construction_contractor);
        }
        const value = resolveContractOrAcfField(
          summary,
          "construction_contractor",
          g?.construction_contractor ?? g?.contractor
        );
        return expansionFormatAcfScalar(fromContracts || value);
      },
    },
    {
      colId: "exp_fiber_contractor",
      headerName: "Fiber contractor",
      filter: true,
      width: 150,
      valueGetter: (p) => {
        const g = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION];
        const summary = p.context?.contractFieldsByProjectId?.[p.data?.id];
        const fromContracts = formatContractorsForRole(summary, "fiber");
        if (summary?.fields && Object.prototype.hasOwnProperty.call(summary.fields, "fiber_contractor")) {
          return expansionFormatAcfScalar(fromContracts || summary.fields.fiber_contractor);
        }
        const value = resolveContractOrAcfField(
          summary,
          "fiber_contractor",
          g?.fiber_contractor ?? g?.contractor_fiber
        );
        return expansionFormatAcfScalar(fromContracts || value);
      },
    },
    {
      colId: "exp_contract_execution_date",
      headerName: "Contract executed",
      filter: true,
      width: 130,
      valueGetter: (p) => {
        const g = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION];
        const value = resolveContractOrAcfField(
          p.context?.contractFieldsByProjectId?.[p.data?.id],
          "contract_execution_date",
          g?.contract_execution_date
        );
        return value ? String(value).slice(0, 10) : "";
      },
    },
    {
      colId: "exp_contract_expiration_date",
      headerName: "Contract expires",
      filter: true,
      width: 130,
      valueGetter: (p) => {
        const g = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION];
        const value = resolveContractOrAcfField(
          p.context?.contractFieldsByProjectId?.[p.data?.id],
          "contract_expiration_date",
          g?.contract_expiration_date
        );
        return value ? String(value).slice(0, 10) : "";
      },
    },
    ...(includeContractGroupCols
      ? [
          {
            colId: "exp_contract_cost",
            headerName: "Contract cost",
            filter: true,
            width: 120,
            valueGetter: (p) => {
              if (p.data?._rowType === "group") return "";
              const g = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION];
              const value = resolveContractOrAcfField(
                p.context?.contractFieldsByProjectId?.[p.data?.id],
                "contract_cost",
                g?.contract_cost
              );
              return value != null && value !== "" ? value : "";
            },
            valueFormatter: (p) => formatGridCurrency(p.value),
          },
        ]
      : []),
    {
      colId: "exp_asbuilt",
      headerName: "As-built",
      width: 72,
      valueGetter: (p) => {
        const g = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION];
        return g?.asbuilt ?? g?.asbuilts_redlines ?? "";
      },
    },
    {
      colId: "exp_usa_ticket",
      headerName: "USA ticket",
      width: 72,
      valueGetter: (p) => {
        const g = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.CONSTRUCTION];
        return g?.usa_ticket ?? g?.usa ?? "";
      },
    },
    {
      colId: "exp_splicer",
      headerName: "Splicer",
      filter: true,
      width: 140,
      valueGetter: (p) => {
        const fib = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.FIBER];
        const v = fib?.splicer;
        return v != null ? String(v).trim() : "";
      },
    },
    {
      colId: "exp_fiber_otdr",
      headerName: "Fiber OTDR",
      filter: true,
      width: 120,
      valueGetter: (p) => {
        const fib = expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.FIBER];
        return formatExpansionFiberOtdrSummary(fib?.fiber_files);
      },
    },
    {
      colId: "exp_completion_notice",
      headerName: "Completion notice",
      width: 72,
      valueGetter: (p) =>
        expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ACCOUNTING]?.completion_notice ?? "",
    },
    {
      colId: "exp_invoiced",
      headerName: "Invoiced",
      filter: true,
      width: 110,
      valueGetter: (p) => expansionDomains(p.data)?.[COMMERCIAL_DOMAINS.ACCOUNTING]?.invoiced ?? "",
    },
  ];

  return cols.map((def) => {
    const colId = String(def.colId ?? def.field ?? "");
    let next = def;
    if (EXPANSION_NUMERIC_STRING_SORT_COL_IDS.has(colId)) {
      next = { ...next, comparator: agGridNumericStringComparator };
    } else if (EXPANSION_NUMBER_SORT_COL_IDS.has(colId)) {
      next = { ...next, comparator: agGridNumberComparator };
    }
    if (next.field === "phase" || next.cellRenderer) {
      return next;
    }
    return { ...next, cellRenderer: UrlOrValueCell };
  });
}

/** Column ids + labels for User Matrix → Expansion table visibility (schema section order). */
const EXPANSION_DESCRIPTOR_SECTIONS = [
  {
    id: "subsegment_summary",
    label: "Subsegment summary",
    colIds: [
      "work_order",
      "exp_segment",
      "exp_project_name",
      "exp_status",
      "phase",
      "active_subphase",
      "exp_departments",
      "exp_bid_out",
      "exp_construction_footage",
      "exp_override_footage",
      "exp_caltrans_footage",
      "exp_handholes",
      "exp_completed_conduit_footage",
      "exp_completed_fiber_footage",
    ],
  },
  {
    id: "engineering_details",
    label: "Engineering details",
    hint: "When Engineering is selected in departments",
    colIds: [
      "exp_route",
      "exp_drafter",
      "exp_permitting_specialist",
      "exp_permits",
      "exp_permit_received_date",
      "exp_permit_expiration_date",
      "exp_refundable_deposit",
      "exp_deposit_amount",
      "exp_permit_status",
      "exp_revision_requested_date",
      "exp_permit_follow_up",
      "exp_materials_summary",
      "exp_materials_review",
    ],
  },
  {
    id: "construction_details",
    label: "Construction details",
    hint: "When Construction is selected in departments",
    colIds: [
      "exp_inspector",
      "exp_construction_contractor",
      "exp_fiber_contractor",
      "exp_asbuilt",
      "exp_usa_ticket",
    ],
  },
  {
    id: "fiber_details",
    label: "Fiber details",
    hint: "When Fiber is selected in departments",
    colIds: ["exp_splicer", "exp_fiber_otdr"],
  },
  {
    id: "accounting_details",
    label: "Accounting details",
    colIds: ["exp_completion_notice", "exp_invoiced"],
  },
];

export function getExpansionGridColumnDescriptorSections() {
  const defs = buildExpansionColumnDefs({
    linkRenderer: () => null,
    displayPhaseForRow: () => "",
    phaseCellClass: () => "",
    phaseCellStyle: () => null,
    subphaseCellStyle: () => null,
    includeMaterialsCols: materialsInventoryPluginEnabled(),
  });
  const byId = new Map(
    defs.map((c) => {
      const colId = String(c.colId ?? c.field ?? "");
      return [colId, { colId, headerName: c.headerName || c.field || colId || "" }];
    })
  );
  return EXPANSION_DESCRIPTOR_SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
    hint: section.hint || "",
    descriptors: section.colIds.map((id) => byId.get(id)).filter(Boolean),
  })).filter((s) => s.descriptors.length > 0);
}

export function getExpansionGridColumnDescriptors() {
  return getExpansionGridColumnDescriptorSections().flatMap((s) => s.descriptors);
}
